import React from "react";
import { Cable, Mic, Music2 } from "lucide-react";
import type { PracticeMode } from "../../contexts/PracticeModeContext";
import type { GuitarInputMode, SongDetectionProfile } from "../../lib/songDetectionProfile";
import { practiceAllowsMicScoring } from "../../lib/songDetectionProfile";

export interface SongInputGuidanceBannerProps {
  profile: SongDetectionProfile;
  mode: PracticeMode;
  inputMode: GuitarInputMode;
  practiceScoringEnabled: boolean;
  onOpenSettings?: () => void;
}

export const SongInputGuidanceBanner: React.FC<SongInputGuidanceBannerProps> = ({
  profile,
  mode,
  inputMode,
  practiceScoringEnabled,
  onOpenSettings,
}) => {
  const micPracticeBlocked =
    mode === "practice" &&
    inputMode === "mic" &&
    !practiceAllowsMicScoring(profile, "mic");

  const learnDenseHint =
    mode === "learn" &&
    inputMode === "mic" &&
    (profile.classification === "dense-poly" || profile.maxVoicesPerAttack >= 3);

  if (micPracticeBlocked) {
    return (
      <BannerShell
        icon={<Music2 className="h-4 w-4 shrink-0 text-teal-300" />}
        title="Practicar · solo reproducción"
        body={
          <>
            Esta pieza tiene acordes de hasta{" "}
            <strong>{profile.maxVoicesPerAttack} voces</strong>. Con micrófono, practicar es seguir
            la partitura en tiempo real sin calificar el mic. Usa{" "}
            <strong>Aprender</strong> y toca cada nota del acorde por separado, o conecta la guitarra
            por <strong>cable a la interfaz</strong> y elige «Entrada de línea» en ajustes.
          </>
        }
        actionLabel={onOpenSettings ? "Configurar entrada" : undefined}
        onAction={onOpenSettings}
      />
    );
  }

  if (learnDenseHint) {
    return (
      <BannerShell
        icon={<Mic className="h-4 w-4 shrink-0 text-pink-300" />}
        title="Aprender · acordes con varias voces"
        body={
          <>
            Toca <strong>voz a voz</strong> del acorde (arpegio). El micrófono valida mejor nota a
            nota que un rasgueo simultáneo. Para practicar en continuo con detección, usa cable de
            audio en ajustes.
          </>
        }
        actionLabel={onOpenSettings ? "Entrada de audio" : undefined}
        onAction={onOpenSettings}
      />
    );
  }

  if (mode === "practice" && practiceScoringEnabled && inputMode === "line") {
    return (
      <BannerShell
        icon={<Cable className="h-4 w-4 shrink-0 text-amber-300" />}
        title="Practicar · entrada de línea"
        body="Señal directa activa: la detección en tiempo real funciona mejor que con el micrófono del dispositivo."
      />
    );
  }

  return null;
};

function BannerShell({
  icon,
  title,
  body,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div
      className="mx-4 mt-2 rounded-xl px-4 py-3 text-left shadow-lg backdrop-blur-md"
      style={{
        backgroundColor: "rgba(28, 26, 25, 0.92)",
        border: "1px solid rgba(232, 224, 212, 0.12)",
        color: "#f5f0eb",
      }}
    >
      <div className="flex gap-3">
        {icon}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-1 text-xs leading-relaxed opacity-90">{body}</p>
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className="mt-2 text-xs font-semibold underline underline-offset-2 opacity-95 hover:opacity-100"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
